/**
 * Single accessor for the auth contract the whole app already relies on:
 * `localStorage.userInfo` written at login (see PostLogin) and read by
 * ProtectedRoute / Sidebar via raw JSON.parse. Admin code must go through
 * this module instead of parsing localStorage inline.
 */
export interface CurrentUser {
  user_id?: number;
  user_type?: number;
  accessToken?: string;
  name?: string;
  email?: string;
  picture?: string;
  [key: string]: unknown;
}

export const getCurrentUser = (): CurrentUser | null => {
  try {
    return JSON.parse(localStorage.getItem("userInfo") as string);
  } catch (e) {
    return null;
  }
};

/** Mirrors the isAuthenticated() logic in App.tsx. */
export const isAuthenticated = (): boolean => {
  const expiresAt = Number(localStorage.getItem("expiresAt") || 0);
  const user = getCurrentUser();
  return !!(user && new Date().getTime() < expiresAt && user.user_type && user.user_id);
};

export const useCurrentUser = (): CurrentUser | null => getCurrentUser();

/**
 * Roles 7, 8 and 9 are 2, 3 and 4 with the forms tool switched on. Collapse them
 * onto the role they copy for every permission decision, so the existing
 * `allowed` lists keep working untouched and none can be forgotten. The forms
 * helpers below are the only ones that read the stored role.
 */
const FORMS_ROLE_BASE: Record<number, number> = { 7: 2, 8: 3, 9: 4 };

export const baseUserType = (userType: unknown): number => {
  const value = Number(userType) || 0;
  return FORMS_ROLE_BASE[value] ?? value;
};

/** The caller's role for permission checks, with 7/8/9 collapsed. */
export const getEffectiveUserType = (user?: CurrentUser | null): number =>
  baseUserType((user === undefined ? getCurrentUser() : user)?.user_type);

/** Stored roles that may use the forms tool; mirrors the server's list. */
export const FORMS_TOOL_USER_TYPES = [1, 7, 8, 9];
/** Of those, the ones that may read submissions (7 copies 2, which had it). */
export const FORMS_INBOX_USER_TYPES = [1, 7];

export const canUseFormsTool = (user?: CurrentUser | null): boolean =>
  FORMS_TOOL_USER_TYPES.includes(
    Number((user === undefined ? getCurrentUser() : user)?.user_type) || 0
  );

export const canReviewFormResponses = (user?: CurrentUser | null): boolean =>
  FORMS_INBOX_USER_TYPES.includes(
    Number((user === undefined ? getCurrentUser() : user)?.user_type) || 0
  );

/**
 * The shared counter-tablet login for Work Orders. It never enters the admin
 * shell: App.tsx gives it its own /work-orders area, and the API authorizer
 * limits its token to the work-order routes.
 */
export const STORE_DEVICE_USER_TYPE = 10;

export const isStoreDevice = (user?: CurrentUser | null): boolean =>
  Number((user === undefined ? getCurrentUser() : user)?.user_type) === STORE_DEVICE_USER_TYPE;

/**
 * A maintenance-only account (restaurant staff, DMs, AP users who use restaurant
 * maintenance and nothing else). It never enters the admin shell: App.tsx gives
 * it its own /maintenance area, and the API authorizer limits its token to the
 * /maintenance routes.
 */
export const MAINTENANCE_USER_TYPE = 11;

export const isMaintenanceOnly = (user?: CurrentUser | null): boolean =>
  Number((user === undefined ? getCurrentUser() : user)?.user_type) === MAINTENANCE_USER_TYPE;

/** Organizations with Work Orders switched on (CT654). Mirrors wo_location_settings in the API. */
export const WORK_ORDER_ORG_IDS = [51];

/** Stored roles that may open Work Orders from the admin shell (6, BI, is excluded; 10 has its own area). */
export const WORK_ORDER_USER_TYPES = [1, 2, 3, 4, 5, 7, 8, 9];

export const canUseWorkOrders = (user?: CurrentUser | null): boolean => {
  const current = user === undefined ? getCurrentUser() : user;
  if (!current) return false;
  const stored = Number(current.user_type) || 0;
  if (WORK_ORDER_USER_TYPES.indexOf(stored) === -1) return false;
  if (stored === 1) return true;
  return WORK_ORDER_ORG_IDS.indexOf(Number(current.organizationId)) !== -1;
};
